// Cómo se CUENTA una ampliación de disco. El backend ya no responde un sí/no:
// devuelve `sync` con lo que pasó de verdad con el volumen en el cluster, y los
// tres portales (admin, partner y cliente) tienen que decir lo mismo — si cada
// uno improvisara su mensaje, el cliente que ve «ampliado» y el partner que ve
// «pendiente» estarían mirando el mismo disco.
//
// Reglas del puente, que son las que dan sentido a los tres mensajes:
//  · applied  → el disco creció (el empleado se redesplegó con el PVC nuevo).
//  · deferred → el empleado no está desplegado; el tamaño queda apuntado y lo
//               estrena en su próximo despliegue. NO es un error.
//  · failed   → el tenant no lo aceptó: NO se apuntó ni se factura nada, así
//               que reintentar es seguro (y es lo accionable).
import { StorageSync } from '../types/clientAccount';

// `t` de i18next, tipado por estructura para no atarse a su firma completa:
// aquí solo se necesita clave + texto por defecto en español.
type Translate = (key: string, defaultValue: string) => string;

export interface StorageSyncFeedback {
  severity: 'success' | 'warning' | 'error';
  message: string;
  /** true = no hubo cambio real: el diálogo debe quedarse abierto para reintentar. */
  failed: boolean;
}

export const storageSyncFeedback = (
  sync: StorageSync | null | undefined,
  t: Translate
): StorageSyncFeedback => {
  // Sin `sync` (backend anterior al puente) solo podemos afirmar lo que sabemos:
  // que quedó apuntado. Es exactamente el mensaje que se daba antes, ni más ni
  // menos — prometer que el disco creció sería inventárselo.
  if (!sync) {
    return {
      severity: 'success',
      failed: false,
      message: t(
        'storageSync.unknown',
        'Almacenamiento ampliado. Se aplicará al reiniciar el empleado.'
      )
    };
  }
  const detail = (sync.detail || '').trim();
  if (sync.status === 'applied') {
    return {
      severity: 'success',
      failed: false,
      message: t(
        'storageSync.applied',
        'Almacenamiento ampliado: el disco del empleado ya ha crecido.'
      )
    };
  }
  if (sync.status === 'deferred') {
    const base = t(
      'storageSync.deferred',
      'Almacenamiento ampliado en la ficha, pero el disco todavía no: el empleado no está desplegado y lo estrenará en su próximo despliegue.'
    );
    return { severity: 'warning', failed: false, message: detail ? `${base} (${detail})` : base };
  }
  const base = t(
    'storageSync.failed',
    'No se pudo ampliar el disco del empleado, así que no se ha guardado ni se cobrará nada. Puedes reintentarlo.'
  );
  return { severity: 'error', failed: true, message: detail ? `${base} ${detail}` : base };
};
